/**
 * FileListDirectory — a set of individually chosen files, presented as a folder.
 *
 * Photographers do not always have a tidy card to point at: sometimes the keepers
 * are a handful of files sitting loose on the desktop. The rest of m3cull only
 * knows how to scan an FsDirectory, so rather than teach every layer about a
 * second shape, loose files are wrapped to look like a flat folder.
 *
 * Read-only: there is no directory to write into, so export must choose its own
 * destination (which it always does anyway).
 */

import { BrowserFsFile } from './browser-fs';
import { FileMissingError, type FsDirectory, type FsEntry, type FsFile } from './fs-port';

export class FileListDirectory implements FsDirectory {
  /** relPath → handle. Names are made unique so two "IMG_1234.JPG" both survive. */
  private readonly byPath: Map<string, FileSystemFileHandle>;

  constructor(
    readonly name: string,
    handles: FileSystemFileHandle[],
  ) {
    this.byPath = new Map();
    for (const handle of handles) {
      this.byPath.set(uniqueKey(handle.name, this.byPath), handle);
    }
  }

  /** The handles, in insertion order — needed to persist the selection. */
  get handles(): FileSystemFileHandle[] {
    return [...this.byPath.values()];
  }

  get size(): number {
    return this.byPath.size;
  }

  async list(): Promise<FsEntry[]> {
    return [...this.byPath.keys()].map((name) => ({ kind: 'file', name }) as FsEntry);
  }

  /** A flat selection has no subfolders. */
  async directory(name: string): Promise<FsDirectory> {
    throw new FileMissingError(name);
  }

  async file(relPath: string): Promise<FsFile> {
    const handle = this.byPath.get(relPath);
    if (!handle) throw new FileMissingError(relPath);

    try {
      return await new BrowserFsFile(handle).prime();
    } catch {
      throw new FileMissingError(relPath);
    }
  }

  async has(relPath: string): Promise<boolean> {
    return this.byPath.has(relPath);
  }

  /** Parameters are declared to match FsDirectory, even though nothing uses them. */
  async write(_name: string, _bytes: Uint8Array): Promise<void> {
    throw new Error('A hand-picked selection has no folder to write into.');
  }
}

/** "IMG_1.JPG" taken → "IMG_1 (2).JPG", so same-named files from different folders coexist. */
function uniqueKey(name: string, taken: Map<string, unknown>): string {
  if (!taken.has(name)) return name;

  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';

  for (let n = 2; n < 100_000; n++) {
    const candidate = `${stem} (${n})${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${stem} (${taken.size})${ext}`;
}
